/* eslint-disable filename-rules/match */
import { overlay } from "overlay-kit";
import { useCallback, useMemo } from "react";

import { useJoinAsSectionAssistant } from "./hooks/use-join-as-section-assistant";
import { usePageActions } from "./page";
import { useQueryParam, useQueryParamsActions } from "./routing/hooks/use-query-params";
import { getNavigationValues, pageUrl } from "./urls";

import { AssessmentStatus } from "api/api-data-types";
import { serverReleaseAssessments } from "api/api-server-actions";
import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import { OutcomeScoreTableData, SectionScoreTableRow } from "api/api-types-assessment";
import {
  ACCESS_ID_QUERY_PARAM,
  ASSESSMENT_VIEW_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
} from "components/constants";
import {
  LAST_ASSESSMENT_PANEL_ACCESS_ID,
  getTotalSubmissions,
} from "components/materials/page/assessment/helpers";
import { ResponseType, useResponsesForPage } from "components/materials/page/responses";
import { OrgType, RubricType, SectionScoreDisplayFormat } from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectCurrentPageId,
  selectCurrentSectionId,
  selectCurrentSectionIsArchived,
  selectDefaultSubmissionIdForUser,
  selectFirstAssessableResponseId,
  selectIsUnassessablePage,
  selectOfficialSubmissionCountByPageId,
  selectSubmissionsByPageId,
} from "store/selectors";
import { selectPageGroupUsersByPageId } from "store/selectors/page-groups";
import { computedStateActions } from "store/slices/computed";
import { localStateActions } from "store/slices/local";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { stopPresenting, usePresentation } from "utils/presentation";

export const getMaxSectionScoreForOrg = (
  org: Pick<OrgType, "section_score_display_format">,
): number => {
  return {
    [SectionScoreDisplayFormat.Percent]: 100,
    [SectionScoreDisplayFormat.Scale_4]: 4,
    [SectionScoreDisplayFormat.Scale_5]: 5,
  }[org.section_score_display_format];
};

const roundHalfUpToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

/** Convert a 0–100 percent to a rubric score via linear interpolation of conversion_percent. */
export const getRubricScoreFromPercent = (percent: number, rubric: RubricType): number | null => {
  if (percent < 0 || rubric.length < 2) return null;

  const clampedPercent = Math.min(percent, 100);

  for (let i = 0; i < rubric.length - 1; i += 1) {
    const lower = rubric[i];
    const upper = rubric[i + 1];

    if (lower.conversion_percent <= clampedPercent && clampedPercent <= upper.conversion_percent) {
      const span = upper.conversion_percent - lower.conversion_percent;
      if (span === 0) return roundHalfUpToOneDecimal(lower.score);

      const fraction = (clampedPercent - lower.conversion_percent) / span;
      return roundHalfUpToOneDecimal(lower.score + fraction * (upper.score - lower.score));
    }
  }

  return null;
};

type AssessmentCreateOrUpdateType = {
  points: number | null | undefined;
  comment: string;
  page_outcomes: Record<string, number>;
  is_official: boolean;
};

const createAssessment = (
  params: AssessmentCreateOrUpdateType,
  assessedById: string,
  submissionId: string,
  sectionId: string,
) => {
  return storeApi.assessments.create({
    points: params.points,
    comment: params.comment,
    page_outcomes: params.page_outcomes,
    assessed_by_id: assessedById,
    section_id: sectionId,
    submission_id: submissionId,
    is_official: params.is_official,
  });
};

const updateAssessment = (
  assessmentId: string,
  params: AssessmentCreateOrUpdateType,
  assessedById: string,
  submissionId: string,
) => {
  return storeApi.assessments.partial_update({
    id: assessmentId,
    points: params.points,
    comment: params.comment,
    page_outcomes: params.page_outcomes,
    assessed_by_id: assessedById,
    submission_id: submissionId,
    is_official: params.is_official,
  });
};

const deleteAssessment = (assessmentId: string) => {
  return storeApi.assessments.destroy(assessmentId).then(() => {
    toastSuccessMessage(t("success.toasts.delete_success", { item: t(`glossary.assessment`) }));
  });
};

export const useAssessmentActions = () => {
  return { createAssessment, updateAssessment, deleteAssessment };
};

export const useAssessmentMode = (pageId: string | null) => {
  const dispatch = useAppDispatch();
  const { navigateToPage } = usePageActions();
  const { setQueryParams } = useQueryParamsActions();
  const sectionId = useAppSelector(selectCurrentSectionId);

  const page = useObjectSelector(
    (state) => pageSelectors.selectById(state, pageId),
    ["id", "released_at", "assessments_published_at"],
  );
  const { isPresenting, presentedSessionId } = usePresentation();
  const currentPageId = useAppSelector(selectCurrentPageId);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isUnassessable = useAppSelector((s) => selectIsUnassessablePage(s, pageId));
  const { joinAsSectionAssistantIfNew } = useJoinAsSectionAssistant();

  const startAssessing = useCallback(async () => {
    // Archived sections are read-only and cannot enter assessment mode.
    if (!pageId || isArchived || isUnassessable) return;

    if (isPresenting && presentedSessionId) {
      const shouldContinue = await overlay.openAsync<boolean>(({ isOpen, close }) => (
        <ConfirmationDialog
          cancelButtonText={t("common.cancel")}
          className="confirm-modal z-[10001]"
          confirmButtonText={t("common.continue")}
          description={t("assessment.stop_presenting_for_assessment_modal.description")}
          open={isOpen}
          title={t("assessment.stop_presenting_for_assessment_modal.title")}
          onClose={() => close(false)}
          onConfirm={() => close(true)}
        />
      ));

      if (!shouldContinue) {
        return;
      }
      await stopPresenting(presentedSessionId);
    }

    await joinAsSectionAssistantIfNew();

    dispatch(localStateActions.setIsEditMode(false));
    setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: null });

    if (currentPageId !== pageId) {
      const href = pageUrl(sectionId!, pageId, {
        additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
      });
      navigateToPage(page, href);
    } else {
      setQueryParams({
        [ASSESSMENT_VIEW_QUERY_PARAM]: "1",
        [SECONDARY_OVERLAY_QUERY_PARAM]: null,
        [SUBMISSION_ID_QUERY_PARAM]: null,
      });
    }
  }, [
    isPresenting,
    presentedSessionId,
    dispatch,
    pageId,
    page,
    currentPageId,
    sectionId,
    isArchived,
    isUnassessable,
    navigateToPage,
    setQueryParams,
    joinAsSectionAssistantIfNew,
  ]);

  const stopAssessing = useCallback(
    async (additionalParams?: Record<string, string | null>) => {
      if (!pageId) return;
      const statuses = await serverGetPageAssessmentStatuses({ pageIds: [pageId] });
      dispatch(computedStateActions.savePageAssessmentStatuses(statuses));

      setQueryParams({
        [ACCESS_ID_QUERY_PARAM]: null,
        [ASSESSMENT_VIEW_QUERY_PARAM]: null,
        [SUBMISSION_ID_QUERY_PARAM]: null,
        ...additionalParams,
      });
    },
    [setQueryParams, dispatch, pageId],
  );

  const stopAssessingAndMaybeShowAssessmentSummary = () => {
    stopAssessing(
      page?.assessments_published_at
        ? { [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AssessmentSummary }
        : undefined,
    );
  };

  return {
    startAssessing,
    stopAssessing,
    stopAssessingAndMaybeShowAssessmentSummary,
  };
};

export const useAssessmentPublishActions = (pageId: string | null) => {
  const dispatch = useAppDispatch();
  const { setQueryParams } = useQueryParamsActions();
  const publishGrades = async () => {
    if (!pageId) return;
    await serverReleaseAssessments(pageId);
    toastSuccessMessage(t("success.toasts.publish_assessments_success"));
  };

  const unpublishGrades = () => {
    if (!pageId) return;
    storeApi.pages.partial_update({ id: pageId, assessments_published_at: null });
    dispatch(
      computedStateActions.savePageAssessmentStatuses({
        [pageId]: {
          status: AssessmentStatus.ASSESSABLE,
          unassessed_late_submissions_count: 0,
          assessed_submissions_count: 0,
          unseen_late_submissions_count: 0,
          unseen_submissions_count: 0,
        },
      }),
    );
    setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: null });
  };

  return { publishGrades, unpublishGrades };
};

export type AssessmentCountData = {
  count: number;
  total: number;
};

export type AssessmentCountDataWithNotSubmitted = AssessmentCountData & {
  notSubmittedCount: number;
};

/**
 * Hook that returns count and total without calculating notSubmittedCount.
 * Use this when you don't need notSubmittedCount.
 */
export const useAssessedCountData = (pageId: string | null): AssessmentCountData => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);
  // Submissions must be already loaded by the parent components before using this hook.
  const submissions = useListSelector((s) => selectSubmissionsByPageId(s, pageId));
  const officialSubmissionCount = useAppSelector((s) =>
    selectOfficialSubmissionCountByPageId(s, pageId),
  );
  const enabledPageGroupUsers = useListSelector((s) => selectPageGroupUsersByPageId(s, pageId));

  if (!page) return { count: 0, total: 0 };

  const total = getTotalSubmissions(page, submissions, enabledPageGroupUsers);

  return { count: officialSubmissionCount, total };
};

/**
 * Hook that returns count, total, and notSubmittedCount.
 * Use this when you need notSubmittedCount, but be aware it causes re-renders when a new user types in the doc because
 *  of useWorksheetAccessIds.
 */
export const useAssessedCountDataWithNotSubmitted = (
  pageId: string | null,
): AssessmentCountDataWithNotSubmitted => {
  const { count, total } = useAssessedCountData(pageId);
  const { responses } = useResponsesForPage(pageId);
  const notSubmittedCount = responses.filter((r) => r.id).length - total;
  return { count, total, notSubmittedCount };
};

export const useFirstAssessableResponseId = (pageId: string | null, responses: ResponseType[]) => {
  const responseIds = useMemo(() => responses.map((response) => response.id), [responses]);
  return useAppSelector((s) => selectFirstAssessableResponseId(s, pageId, responseIds));
};

export const useAssessmentNavData = (pageId: string | null) => {
  const { setQueryParams } = useQueryParamsActions();
  const { responses } = useResponsesForPage(pageId);

  const panels = useMemo(
    () => responses.map((response) => response.id).concat([LAST_ASSESSMENT_PANEL_ACCESS_ID]),
    [responses],
  );

  const firstId = useFirstAssessableResponseId(pageId, responses);
  const accessParamId = useQueryParam(ACCESS_ID_QUERY_PARAM);

  const currentId = accessParamId || firstId;
  const isLastPanel = currentId === LAST_ASSESSMENT_PANEL_ACCESS_ID;

  const { nextId, prevId, nextURL, prevURL } = useMemo(
    () =>
      getNavigationValues(panels, {
        queryParam: ACCESS_ID_QUERY_PARAM,
        currentId,
      }),
    [panels, currentId],
  );

  const nextSubmissionId = useAppSelector((s) =>
    selectDefaultSubmissionIdForUser(s, pageId ?? "", nextId, true),
  );

  const prevSubmissionId = useAppSelector((s) =>
    selectDefaultSubmissionIdForUser(s, pageId ?? "", prevId, true),
  );

  const memoizedNextURL = useMemo(() => {
    if (!nextURL) return null;

    const url = new URL(nextURL.toString());

    if (nextSubmissionId) {
      url.searchParams.set(SUBMISSION_ID_QUERY_PARAM, nextSubmissionId);
    }

    url.searchParams.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");
    return url;
  }, [nextURL, nextSubmissionId]);

  const memoizedPrevURL = useMemo(() => {
    if (!prevURL) return null;

    const url = new URL(prevURL.toString());

    if (prevSubmissionId) {
      url.searchParams.set(SUBMISSION_ID_QUERY_PARAM, prevSubmissionId);
    }

    url.searchParams.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");
    return url;
  }, [prevURL, prevSubmissionId]);

  const onBack = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      e?.stopPropagation();
      setQueryParams({
        [ACCESS_ID_QUERY_PARAM]: prevId || null,
        [SUBMISSION_ID_QUERY_PARAM]: prevSubmissionId || null,
      });
    },
    [setQueryParams, prevId, prevSubmissionId],
  );

  const onNext = useCallback(() => {
    setQueryParams({
      [ACCESS_ID_QUERY_PARAM]: nextId || null,
      [SUBMISSION_ID_QUERY_PARAM]: nextSubmissionId || null,
    });
  }, [setQueryParams, nextId, nextSubmissionId]);

  return {
    onNext,
    onBack,
    nextURL: memoizedNextURL,
    prevURL: memoizedPrevURL,
    prevId,
    nextId,
    currentId,
    isLastPanel,
    nextSubmissionId,
    prevSubmissionId,
  };
};

/**
 * Helper method for components that need to render an empty state when section
 * score data is unavailable. The API endpoint always produces some response data
 * even if no assessments have been done, so we have to scan through each row to
 * check for null values.
 */
export const isSectionScoreDataSufficient = (
  sectionScoreTableRows: SectionScoreTableRow[],
): boolean => {
  return sectionScoreTableRows.some(
    (row) =>
      row.prep_work_score !== null ||
      row.activities_score !== null ||
      row.assignments_score !== null,
  );
};

export const isOutcomeScoreTableDataSufficient = (
  outcomeScoreTableData: OutcomeScoreTableData,
): boolean => {
  return (
    outcomeScoreTableData.headers.length > 0 &&
    outcomeScoreTableData.rows.length > 0 &&
    outcomeScoreTableData.rows.some((row) => row.outcome_scores.some((score) => score !== null))
  );
};
